import { useState, useEffect, useMemo, useRef } from "react";
import { reportError, showAlert, showUserConfirm } from "../../../api/errors";
import { fileStore } from "../utils/dataStore";
import { mapFile } from "../../../api/loadDataStore";
import type { AssocFile } from "../../../api/types";
import { apiDelete, apiGetText, apiPost, apiPut } from "../../../api/http";
import { notificationStore } from "../utils/notificationStore";
import { useAdminData } from "../AdminDataContext";

/** 与后端 OssStorageService.MaxIngestBytes 一致 */
const MAX_ASSOC_FILE_UPLOAD_BYTES = 15 * 1024 * 1024;

/** 文档编辑保存为 UTF-8 纯文本（后端 OSS 类型 text/plain，避免 html mime 与旧版后端不兼容） */
function buildAssocDocumentPlain(displayTitle: string, plainBody: string): string {
  const title = (displayTitle.trim() || "未命名文档").trim();
  const body = plainBody.replace(/\r\n/g, "\n");
  return `${title}\n\n${body}`;
}

function plainTextToUtf8Base64DataUrl(text: string): string {
  const b64 = btoa(unescape(encodeURIComponent(text)));
  return `data:text/plain;charset=utf-8;base64,${b64}`;
}

/** 与 buildAssocDocumentPlain 对称：剥离已存储文件中的「标题 + 换行」前缀，仅保留正文段供继续编辑 */
function stripStoredPlainTitle(raw: string, fileTitle: string): string {
  let t = raw.replace(/\r\n/g, "\n");
  if (t.charCodeAt(0) === 0xfeff) t = t.slice(1);
  t = t.trimEnd();
  const name = fileTitle.trim();
  if (!t) return "";

  const headMatches = (line: string) => line.trim() === name;

  const double = t.match(/^([^\n]*)\n\n([\s\S]*)$/);
  if (double && headMatches(double[1])) return double[2];

  const single = t.match(/^([^\n]*)\n([\s\S]+)$/);
  if (single && headMatches(single[1])) return single[2];

  if (name && t.startsWith(name)) {
    const rest = t.slice(name.length);
    if (rest.startsWith("\n\n")) return rest.slice(2);
    if (rest.startsWith("\n")) return rest.slice(1);
  }

  return t.trim();
}

async function loadPlainBodyFromAssocContentUrl(url: string): Promise<string> {
  try {
    const res = await fetch(url, { credentials: "omit", mode: "cors" });
    if (!res.ok) return "";
    const raw = await res.text();
    const ct = res.headers.get("content-type")?.toLowerCase() ?? "";
    if (ct.includes("text/html")) {
      const doc = new DOMParser().parseFromString(raw, "text/html");
      return (doc.body?.innerText ?? raw).replace(/\r\n/g, "\n").trim();
    }
    return raw.replace(/\r\n/g, "\n").trim();
  } catch {
    return "";
  }
}

/** 优先走管理端 API 从 OSS 读 UTF-8 正文（同域 + JWT），避免预签名 OSS 的 CORS 导致编辑区空白。 */
async function loadEditorPlainBodyForFile(tenantLegacyId: string, file: AssocFile): Promise<string> {
  let fromApi = "";
  try {
    fromApi = await apiGetText(
      `/api/tenants/${encodeURIComponent(tenantLegacyId)}/files/${encodeURIComponent(file.id)}/content-plain`,
    );
    fromApi = fromApi.replace(/\r\n/g, "\n");
  } catch {
    fromApi = "";
  }
  if (fromApi !== "") return fromApi;
  const url = file.contentUrl?.trim();
  if (url) return loadPlainBodyFromAssocContentUrl(url);
  return "";
}

const typeConfig: Record<AssocFile["type"], { label: string; icon: string; color: string }> = {
  contract: { label: "入会协议", icon: "ri-file-text-line", color: "bg-red-50 text-red-600" },
  rulebook: { label: "协会章程", icon: "ri-book-open-line", color: "bg-amber-50 text-amber-600" },
  notice: { label: "通知公告", icon: "ri-megaphone-line", color: "bg-green-50 text-green-600" },
  form: { label: "申请表单", icon: "ri-file-list-3-line", color: "bg-gray-100 text-gray-600" },
  certificate: { label: "证明文件", icon: "ri-award-line", color: "bg-teal-50 text-teal-600" },
  disclaimer: { label: "免责声明", icon: "ri-shield-check-line", color: "bg-slate-50 text-slate-700" },
};

const statusConfig = {
  published: { label: "已发布", color: "bg-green-50 text-green-700" },
  draft: { label: "草稿", color: "bg-amber-50 text-amber-700" },
  archived: { label: "已归档", color: "bg-gray-100 text-gray-500" },
};

export default function FileManager() {
  const { tenantLegacyId, storeVersion, reload } = useAdminData();
  const [files, setFiles] = useState<AssocFile[]>(() => fileStore.getAll());
  const [selectedFile, setSelectedFile] = useState<AssocFile | null>(null);

  useEffect(() => {
    setFiles(fileStore.getAll());
  }, [storeVersion]);

  useEffect(() => {
    return fileStore.subscribe(() => setFiles(fileStore.getAll()));
  }, []);

  useEffect(() => {
    setSelectedFile((prev) => {
      if (!prev) return null;
      return files.find((f) => f.id === prev.id) ?? null;
    });
  }, [files]);
  const [filterType, setFilterType] = useState<"all" | AssocFile["type"]>("all");
  const [filterStatus, setFilterStatus] = useState<"all" | AssocFile["status"]>("all");
  const [showDocEditor, setShowDocEditor] = useState(false);
  const [docEditorTarget, setDocEditorTarget] = useState<AssocFile | null>(null);
  const [editorPlainBody, setEditorPlainBody] = useState("");
  const [loadingEditorBody, setLoadingEditorBody] = useState(false);
  const [showSignPreview, setShowSignPreview] = useState<AssocFile | null>(null);
  const [signDone, setSignDone] = useState(false);
  const [savingDoc, setSavingDoc] = useState(false);
  const [saveDocDone, setSaveDocDone] = useState(false);
  const [showRemind, setShowRemind] = useState<AssocFile | null>(null);
  const [remindSent, setRemindSent] = useState(false);
  const [remindSending, setRemindSending] = useState(false);
  const [newFile, setNewFile] = useState({
    name: "",
    type: "contract" as AssocFile["type"],
    description: "",
    version: "",
    requireSignature: false,
    showToMembers: true,
  });

  const editorLoadSeq = useRef(0);
  const docEditorPreviewPlain = useMemo(
    () => buildAssocDocumentPlain(newFile.name.trim() || "未命名文档", editorPlainBody),
    [newFile.name, editorPlainBody],
  );

  const filtered = files.filter((f) => {
    const matchType = filterType === "all" || f.type === filterType;
    const matchStatus = filterStatus === "all" || f.status === filterStatus;
    return matchType && matchStatus;
  });

  // counts per type
  const typeCounts = (["all", "contract", "rulebook", "notice", "form", "certificate", "disclaimer"] as const).reduce(
    (acc, t) => {
      acc[t] = t === "all" ? files.length : files.filter((f) => f.type === t).length;
      return acc;
    },
    {} as Record<string, number>
  );

  const handleToggleVisible = async (id: string) => {
    const current = files.find((f) => f.id === id);
    if (!current) return;
    const next = !current.showToMembers;
    try {
      await apiPut(`/api/tenants/${tenantLegacyId}/files/${id}`, { showToMembers: next });
      await reload();
    } catch (e) {
      reportError(e, "更新失败");
    }
  };

  const handlePublish = async (id: string) => {
    try {
      await apiPut(`/api/tenants/${tenantLegacyId}/files/${id}`, { status: "published" });
      await reload();
    } catch (e) {
      reportError(e, "发布失败");
    }
  };

  const handleArchive = async (id: string) => {
    try {
      await apiPut(`/api/tenants/${tenantLegacyId}/files/${id}`, { status: "archived" });
      await reload();
    } catch (e) {
      reportError(e, "归档失败");
    }
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const openNewDocumentModal = () => {
    editorLoadSeq.current += 1;
    setDocEditorTarget(null);
    setNewFile({
      name: "",
      type: "contract",
      description: "",
      version: "",
      requireSignature: false,
      showToMembers: true,
    });
    setEditorPlainBody("");
    setLoadingEditorBody(false);
    setShowDocEditor(true);
  };

  const openEditDocumentModal = async (file: AssocFile) => {
    const seq = ++editorLoadSeq.current;
    setDocEditorTarget(file);
    setNewFile({
      name: file.name,
      type: file.type,
      description: file.description,
      version: file.version,
      requireSignature: file.requireSignature,
      showToMembers: file.showToMembers,
    });
    setEditorPlainBody("");
    setShowDocEditor(true);
    setLoadingEditorBody(true);
    try {
      const text = await loadEditorPlainBodyForFile(tenantLegacyId, file);
      if (seq !== editorLoadSeq.current) return;
      setEditorPlainBody(stripStoredPlainTitle(text, file.name));
    } finally {
      if (seq === editorLoadSeq.current) setLoadingEditorBody(false);
    }
  };

  const closeDocEditor = () => {
    editorLoadSeq.current += 1;
    setShowDocEditor(false);
    setDocEditorTarget(null);
    setEditorPlainBody("");
    setLoadingEditorBody(false);
  };

  const handleSaveDocument = async () => {
    if (!newFile.name.trim()) {
      showAlert("请填写文件名称。");
      return;
    }
    const plain = buildAssocDocumentPlain(newFile.name.trim(), editorPlainBody);
    const byteLen = new TextEncoder().encode(plain).length;
    if (byteLen > MAX_ASSOC_FILE_UPLOAD_BYTES) {
      showAlert(`正文过大，请控制在约 ${MAX_ASSOC_FILE_UPLOAD_BYTES / (1024 * 1024)}MB 以内。`);
      return;
    }
    const contentDataUrl = plainTextToUtf8Base64DataUrl(plain);
    const sizeLabel = formatFileSize(byteLen);

    setSavingDoc(true);
    setSaveDocDone(false);
    try {
      let raw: Record<string, unknown>;
      if (docEditorTarget) {
        raw = (await apiPut<Record<string, unknown>>(
          `/api/tenants/${tenantLegacyId}/files/${encodeURIComponent(docEditorTarget.id)}`,
          {
            contentDataUrl,
            sizeLabel,
            showToMembers: newFile.showToMembers,
          },
        )) as Record<string, unknown>;
      } else {
        raw = (await apiPost<Record<string, unknown>>(`/api/tenants/${tenantLegacyId}/files`, {
          name: newFile.name.trim(),
          type: newFile.type,
          description: newFile.description,
          version: newFile.version.trim() || "v1.0",
          showToMembers: newFile.showToMembers,
          requireSignature: newFile.requireSignature,
          sizeLabel,
          uploader: "超级管理员",
          contentDataUrl,
        })) as Record<string, unknown>;
      }
      const mapped = mapFile(raw);
      fileStore.upsert(mapped);
      setSelectedFile((prev) => (prev && prev.id === mapped.id ? mapped : prev));
      setSaveDocDone(true);
      setTimeout(() => {
        setSaveDocDone(false);
        closeDocEditor();
      }, 700);
      try {
        await reload();
      } catch {
        // AdminDataContext 全量 reload 失败时，列表已由 fileStore.upsert 更新
      }
    } catch (e) {
      reportError(e, docEditorTarget ? "保存文档失败" : "创建文档失败");
    } finally {
      setSavingDoc(false);
    }
  };

  const handleDeleteFile = async (file: AssocFile) => {
    const ok = await showUserConfirm(
      `确定删除「${file.name}」吗？将永久移除该文件及全部签署记录，不可恢复。`,
      { confirmText: "删除", cancelText: "取消" },
    );
    if (!ok) return;
    try {
      await apiDelete(`/api/tenants/${tenantLegacyId}/files/${encodeURIComponent(file.id)}`);
      await reload();
      setSelectedFile((prev) => (prev?.id === file.id ? null : prev));
    } catch (e) {
      reportError(e, "删除失败");
    }
  };

  const handleSendRemind = async (file: AssocFile) => {
    setRemindSending(true);
    try {
      await notificationStore.sendSignatureReminder(
        tenantLegacyId,
        file.name,
        file.id,
        file.totalMembers - file.signedCount,
      );
      setRemindSent(true);
      setTimeout(() => {
        setRemindSent(false);
        setShowRemind(null);
      }, 1500);
    } catch (e) {
      reportError(e, "提醒发送失败");
    } finally {
      setRemindSending(false);
    }
  };

  const visibleCount = files.filter((f) => f.showToMembers && f.status === "published").length;
  const signReqCount = files.filter((f) => f.requireSignature && f.status === "published").length;

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-5">
        <div>
          <h2 className="text-lg font-bold text-gray-800">文件管理</h2>
          <p className="text-xs text-gray-400 mt-0.5">以文档编辑方式维护协会文件正文，支持发布给会员查阅或电子签署</p>
        </div>
        <div className="flex items-center gap-2">
          <span className="flex items-center gap-1.5 bg-green-50 text-green-600 text-xs px-3 py-1.5 rounded-xl">
            <i className="ri-eye-line"></i> {visibleCount} 个对会员可见
          </span>
          <span className="flex items-center gap-1.5 bg-amber-50 text-amber-600 text-xs px-3 py-1.5 rounded-xl">
            <i className="ri-pen-nib-line"></i> {signReqCount} 个需签署
          </span>
          <button onClick={openNewDocumentModal} className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white text-sm rounded-xl hover:bg-red-700 cursor-pointer transition-colors whitespace-nowrap">
            <i className="ri-file-add-line"></i> 新建文件
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="space-y-3 mb-4">
        {/* Type filter – icon cards */}
        <div className="flex items-center gap-2 flex-wrap">
          {(["all", "contract", "rulebook", "notice", "form", "certificate", "disclaimer"] as const).map((t) => {
            const isActive = filterType === t;
            const cfg = t !== "all" ? typeConfig[t] : null;
            return (
              <button
                key={t}
                onClick={() => setFilterType(t)}
                className={`flex items-center gap-1.5 px-3 py-2 rounded-xl border text-xs font-medium cursor-pointer transition-all whitespace-nowrap ${
                  isActive
                    ? "bg-red-600 text-white border-red-600"
                    : "bg-white text-gray-600 border-gray-200 hover:border-gray-300"
                }`}
              >
                {cfg && <i className={`${cfg.icon} text-sm`}></i>}
                {t === "all" ? "全部文件" : cfg!.label}
                <span className={`text-xs px-1.5 py-0.5 rounded-full font-bold ${isActive ? "bg-red-500 text-white" : "bg-gray-100 text-gray-500"}`}>
                  {typeCounts[t]}
                </span>
              </button>
            );
          })}
          <div className="ml-auto flex gap-1 bg-gray-100 p-1 rounded-xl">
            {(["all", "published", "draft", "archived"] as const).map((s) => (
              <button key={s} onClick={() => setFilterStatus(s)} className={`px-3 py-1.5 rounded-lg text-xs cursor-pointer transition-all whitespace-nowrap ${filterStatus === s ? "bg-white text-gray-800 font-medium shadow-sm" : "text-gray-500 hover:text-gray-700"}`}>
                {s === "all" ? "全部状态" : statusConfig[s].label}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="flex gap-5">
        {/* File List */}
        <div className="flex-1 min-w-0 space-y-2">
          {filtered.map((file) => {
            const typeCfg = typeConfig[file.type];
            const signRate = file.requireSignature && file.totalMembers > 0
              ? Math.round((file.signedCount / file.totalMembers) * 100)
              : null;
            return (
              <div
                key={file.id}
                onClick={() => setSelectedFile(file)}
                className={`bg-white rounded-2xl p-4 cursor-pointer transition-all border-2 ${selectedFile?.id === file.id ? "border-red-400" : "border-transparent hover:border-gray-200"} ${file.status === "archived" ? "opacity-60" : ""}`}
              >
                <div className="flex items-start gap-3">
                  <div className={`w-10 h-10 flex items-center justify-center rounded-xl flex-shrink-0 ${typeCfg.color}`}>
                    <i className={`${typeCfg.icon} text-lg`}></i>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start gap-2 mb-1 flex-wrap">
                      <span className="text-sm font-semibold text-gray-800 truncate max-w-xs">{file.name}</span>
                      <span className={`text-xs px-1.5 py-0.5 rounded-full font-medium flex-shrink-0 ${statusConfig[file.status].color}`}>{statusConfig[file.status].label}</span>
                      <span className={`text-xs px-1.5 py-0.5 rounded-full flex-shrink-0 ${typeCfg.color}`}>{typeCfg.label}</span>
                    </div>
                    <p className="text-xs text-gray-400 line-clamp-1 mb-2">{file.description}</p>
                    <div className="flex items-center gap-3 flex-wrap text-xs text-gray-400">
                      <span className="flex items-center gap-1"><i className="ri-file-line text-gray-300"></i>{file.size}</span>
                      <span>{file.version}</span>
                      <span>{file.uploadedAt}</span>
                      {file.showToMembers && file.status === "published" && <span className="text-green-600 flex items-center gap-1"><i className="ri-eye-line"></i> 会员可见</span>}
                      {signRate !== null && <span className="text-amber-600 flex items-center gap-1"><i className="ri-pen-nib-line"></i>签署 {file.signedCount}/{file.totalMembers}（{signRate}%）</span>}
                    </div>
                  </div>
                </div>

                {signRate !== null && (
                  <div className="mt-3">
                    <div className="flex justify-between text-xs mb-1 text-gray-400">
                      <span>签署进度</span>
                      <span className={`font-medium ${signRate >= 80 ? "text-green-600" : signRate >= 50 ? "text-amber-600" : "text-red-500"}`}>{signRate}%</span>
                    </div>
                    <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
                      <div className={`h-full rounded-full transition-all ${signRate >= 80 ? "bg-green-500" : signRate >= 50 ? "bg-amber-400" : "bg-red-400"}`} style={{ width: `${signRate}%` }}></div>
                    </div>
                  </div>
                )}

                {/* File action buttons */}
                <div className="flex items-center gap-2 mt-3 pt-3 border-t border-gray-50 flex-wrap">
                  {file.status === "draft" && (
                    <button onClick={(e) => { e.stopPropagation(); void handlePublish(file.id); }} className="flex items-center gap-1 px-3 py-1 bg-green-600 text-white text-xs rounded-lg hover:bg-green-700 cursor-pointer whitespace-nowrap">
                      <i className="ri-send-plane-line"></i> 发布
                    </button>
                  )}
                  <button
                    onClick={(e) => { e.stopPropagation(); void handleToggleVisible(file.id); }}
                    className={`flex items-center gap-1.5 px-3 py-1 text-xs rounded-lg cursor-pointer whitespace-nowrap transition-all ${file.showToMembers ? "bg-green-100 text-green-700 hover:bg-green-200" : "bg-gray-100 text-gray-500 hover:bg-gray-200"}`}
                  >
                    <i className={file.showToMembers ? "ri-eye-line" : "ri-eye-off-line"}></i>
                    {file.showToMembers ? "会员可见" : "仅内部"}
                  </button>
                  {file.requireSignature && file.status === "published" && (
                    <>
                      <button
                        onClick={(e) => { e.stopPropagation(); setSignDone(false); setShowSignPreview(file); }}
                        className="flex items-center gap-1 px-3 py-1 bg-amber-50 text-amber-600 text-xs rounded-lg hover:bg-amber-100 cursor-pointer whitespace-nowrap"
                      >
                        <i className="ri-smartphone-line"></i> 签署预览
                      </button>
                      <button
                        onClick={(e) => { e.stopPropagation(); setRemindSent(false); setShowRemind(file); }}
                        className="flex items-center gap-1 px-3 py-1 bg-red-50 text-red-600 text-xs rounded-lg hover:bg-red-100 cursor-pointer whitespace-nowrap"
                      >
                        <i className="ri-notification-3-line"></i> 提醒未签署
                      </button>
                    </>
                  )}
                  {file.status !== "archived" && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        void openEditDocumentModal(file);
                      }}
                      className="flex items-center gap-1 px-3 py-1 bg-sky-50 text-sky-700 text-xs rounded-lg hover:bg-sky-100 cursor-pointer whitespace-nowrap border border-sky-200"
                    >
                      <i className="ri-edit-2-line"></i> 编辑文档
                    </button>
                  )}
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      void handleDeleteFile(file);
                    }}
                    className={`flex items-center gap-1 px-3 py-1 bg-red-50 text-red-600 text-xs rounded-lg hover:bg-red-100 cursor-pointer whitespace-nowrap border border-red-100 ${file.status === "archived" ? "ml-auto" : ""}`}
                  >
                    <i className="ri-delete-bin-line"></i> 删除
                  </button>
                  {file.status !== "archived" && (
                    <button onClick={(e) => { e.stopPropagation(); void handleArchive(file.id); }} className="flex items-center gap-1 px-3 py-1 bg-gray-100 text-gray-500 text-xs rounded-lg hover:bg-gray-200 cursor-pointer whitespace-nowrap ml-auto">
                      <i className="ri-archive-line"></i> 归档
                    </button>
                  )}
                </div>
              </div>
            );
          })}

          {filtered.length === 0 && (
            <div className="text-center py-16 text-gray-400">
              <div className="w-12 h-12 flex items-center justify-center mx-auto mb-3"><i className="ri-folder-open-line text-4xl text-gray-200"></i></div>
              <p className="text-sm">暂无文件</p>
            </div>
          )}
        </div>

        {/* Detail Panel */}
        {selectedFile && (
          <div className="w-72 flex-shrink-0">
            <div className="bg-white rounded-2xl p-5 sticky top-0">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-bold text-gray-800 text-sm">文件详情</h3>
                <button onClick={() => setSelectedFile(null)} className="w-7 h-7 flex items-center justify-center text-gray-400 hover:text-gray-600 cursor-pointer rounded-lg hover:bg-gray-100"><i className="ri-close-line"></i></button>
              </div>
              <div className={`w-12 h-12 flex items-center justify-center rounded-2xl mb-3 ${typeConfig[selectedFile.type].color}`}>
                <i className={`${typeConfig[selectedFile.type].icon} text-2xl`}></i>
              </div>
              <h4 className="font-bold text-gray-800 text-sm mb-1 leading-snug">{selectedFile.name}</h4>
              <p className="text-xs text-gray-500 mb-4 leading-relaxed">{selectedFile.description}</p>
              <div className="space-y-2 mb-4">
                {[
                  { label: "类型", value: typeConfig[selectedFile.type].label },
                  { label: "状态", value: statusConfig[selectedFile.status].label },
                  { label: "版本", value: selectedFile.version },
                  { label: "大小", value: selectedFile.size },
                  { label: "上传时间", value: selectedFile.uploadedAt },
                  { label: "上传人", value: selectedFile.uploader },
                ].map((item) => (
                  <div key={item.label} className="flex items-center justify-between text-xs">
                    <span className="text-gray-400">{item.label}</span>
                    <span className="text-gray-700 font-medium">{item.value}</span>
                  </div>
                ))}
              </div>
              <div className="space-y-2 mb-4">
                <div className="flex items-center justify-between p-3 bg-gray-50 rounded-xl">
                  <div>
                    <p className="text-xs font-medium text-gray-700">会员可见</p>
                    <p className="text-xs text-gray-400 mt-0.5">开启后会员可在前台查看</p>
                  </div>
                  <button onClick={() => void handleToggleVisible(selectedFile.id)} className={`relative w-10 h-5 rounded-full transition-colors cursor-pointer flex-shrink-0 ${selectedFile.showToMembers ? "bg-green-500" : "bg-gray-300"}`}>
                    <span className={`absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transition-all ${selectedFile.showToMembers ? "left-5" : "left-0.5"}`}></span>
                  </button>
                </div>
                {selectedFile.status !== "archived" && (
                  <button
                    type="button"
                    onClick={() => void openEditDocumentModal(selectedFile)}
                    className="w-full py-2.5 bg-sky-50 text-sky-700 text-sm rounded-xl hover:bg-sky-100 cursor-pointer transition-colors whitespace-nowrap border border-sky-200"
                  >
                    <i className="ri-edit-2-line mr-1.5"></i>编辑文档
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => void handleDeleteFile(selectedFile)}
                  className="w-full py-2.5 bg-white text-red-600 text-sm rounded-xl hover:bg-red-50 cursor-pointer transition-colors whitespace-nowrap border border-red-200"
                >
                  <i className="ri-delete-bin-line mr-1.5"></i>删除文件
                </button>
              </div>
              {selectedFile.requireSignature && (
                <div className="bg-amber-50 rounded-xl p-3 mb-4">
                  <p className="text-xs font-semibold text-amber-700 mb-2">签署情况</p>
                  <div className="flex justify-between text-xs mb-2">
                    <span className="text-amber-600">已签署 {selectedFile.signedCount} 人</span>
                    <span className="text-amber-500">待签署 {selectedFile.totalMembers - selectedFile.signedCount} 人</span>
                  </div>
                  <div className="h-2 bg-amber-100 rounded-full overflow-hidden">
                    <div className="h-full bg-amber-500 rounded-full" style={{ width: `${Math.round((selectedFile.signedCount / selectedFile.totalMembers) * 100)}%` }}></div>
                  </div>
                  <p className="text-xs text-amber-500 mt-1.5">{Math.round((selectedFile.signedCount / selectedFile.totalMembers) * 100)}% 完成率</p>
                </div>
              )}
              {selectedFile.requireSignature && selectedFile.status === "published" && (
                <button
                  onClick={() => { setRemindSent(false); setShowRemind(selectedFile); }}
                  className="w-full py-2.5 bg-red-600 text-white text-sm rounded-xl hover:bg-red-700 cursor-pointer transition-colors whitespace-nowrap mb-2"
                >
                  <i className="ri-notification-3-line mr-1.5"></i>提醒未签署会员
                </button>
              )}
              {selectedFile.status === "published" && selectedFile.requireSignature && (
                <button onClick={() => { setSignDone(false); setShowSignPreview(selectedFile); }} className="w-full py-2.5 bg-amber-50 text-amber-700 text-sm rounded-xl hover:bg-amber-100 cursor-pointer transition-colors whitespace-nowrap">
                  <i className="ri-smartphone-line mr-1.5"></i>预览签署效果
                </button>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Remind Modal */}
      {showRemind && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl w-[520px] p-6">
            <div className="flex items-center justify-between mb-5">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-red-50 rounded-xl flex items-center justify-center flex-shrink-0">
                  <i className="ri-notification-3-line text-red-600 text-lg"></i>
                </div>
                <div>
                  <h3 className="font-bold text-gray-800">提醒未签署会员</h3>
                  <p className="text-xs text-gray-400 mt-0.5">将发送消息通知至消息通知模块</p>
                </div>
              </div>
              <button onClick={() => setShowRemind(null)} className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 cursor-pointer rounded-lg hover:bg-gray-100">
                <i className="ri-close-line text-lg"></i>
              </button>
            </div>

            <div className="bg-gray-50 rounded-xl p-4 mb-4 space-y-2.5 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-500">关联文件</span>
                <span className="text-gray-800 font-medium text-right max-w-[60%] truncate">{showRemind.name}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">待签署人数</span>
                <span className="text-amber-600 font-bold">{showRemind.totalMembers - showRemind.signedCount} 人</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">通知内容</span>
                <span className="text-gray-600 text-xs text-right max-w-[60%] leading-relaxed">
                  系统自动生成签署催促消息
                </span>
              </div>
            </div>

            <div className="bg-red-50 border border-red-100 rounded-xl p-4 mb-5">
              <p className="text-xs font-semibold text-gray-700 mb-1.5">消息预览</p>
              <p className="text-sm font-medium text-gray-800 mb-1">请及时签署：{showRemind.name}</p>
              <p className="text-xs text-gray-600 leading-relaxed">
                协会文件「{showRemind.name}」需要您签署确认。目前仍有 {showRemind.totalMembers - showRemind.signedCount} 位会员尚未完成签署，请登录会员服务页面完成签署。逾期可能影响您的活动报名资格，请尽快处理。
              </p>
              <div className="flex items-center gap-2 mt-2 text-xs text-gray-400">
                <span className="bg-red-100 text-red-600 px-2 py-0.5 rounded-full">签署提醒</span>
                <span>目标：未签署会员（{showRemind.totalMembers - showRemind.signedCount}人）</span>
              </div>
            </div>

            <div className="flex gap-3">
              <button onClick={() => setShowRemind(null)} className="flex-1 py-2.5 text-sm text-gray-600 bg-gray-100 rounded-xl hover:bg-gray-200 cursor-pointer whitespace-nowrap">取消</button>
              <button
                onClick={() => void handleSendRemind(showRemind)}
                disabled={remindSending || remindSent}
                className={`flex-1 py-2.5 text-sm rounded-xl cursor-pointer transition-all whitespace-nowrap ${remindSent ? "bg-green-500 text-white" : remindSending ? "bg-gray-200 text-gray-500 cursor-wait" : "bg-red-600 text-white hover:bg-red-700"}`}
              >
                {remindSent ? (
                  <span className="flex items-center justify-center gap-1.5"><i className="ri-check-line"></i> 通知已发送</span>
                ) : remindSending ? (
                  <span className="flex items-center justify-center gap-1.5"><i className="ri-loader-4-line"></i> 发送中...</span>
                ) : (
                  <span className="flex items-center justify-center gap-1.5"><i className="ri-send-plane-line"></i> 发送提醒通知</span>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Sign Preview Modal */}
      {showSignPreview && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl w-[400px] max-h-[85vh] overflow-y-auto">
            <div className="bg-red-600 rounded-t-2xl px-5 py-4 flex items-center gap-3">
              <button onClick={() => setShowSignPreview(null)} className="w-7 h-7 flex items-center justify-center text-white/70 hover:text-white cursor-pointer rounded-lg hover:bg-white/10"><i className="ri-close-line"></i></button>
              <span className="text-white text-sm font-medium flex-1">会员端签署预览</span>
              <span className="text-white/60 text-xs">模拟</span>
            </div>
            <div className="p-5">
              <div className="flex items-center gap-2 mb-4">
                <div className={`w-9 h-9 flex items-center justify-center rounded-xl ${typeConfig[showSignPreview.type].color}`}>
                  <i className={`${typeConfig[showSignPreview.type].icon} text-lg`}></i>
                </div>
                <div>
                  <p className="text-sm font-bold text-gray-800">{showSignPreview.name}</p>
                  <p className="text-xs text-gray-400">{showSignPreview.version} · {showSignPreview.size}</p>
                </div>
              </div>
              <div className="bg-gray-50 rounded-xl p-4 mb-4 text-xs text-gray-600 leading-relaxed space-y-2 max-h-40 overflow-y-auto">
                <p className="font-semibold text-gray-800">文件说明</p>
                <p>{showSignPreview.description}</p>
                <p>本文件由协会管理员发布，签署代表您已阅读并理解文件内容，同意遵守相关规定。签署后将自动记录至您的会员档案，不可撤销。</p>
              </div>
              {!signDone ? (
                <>
                  <div className="border-2 border-dashed border-gray-200 rounded-xl p-4 mb-4 text-center">
                    <div className="w-10 h-10 flex items-center justify-center bg-gray-100 rounded-xl mx-auto mb-3">
                      <i className="ri-pen-nib-line text-gray-400 text-lg"></i>
                    </div>
                    <p className="text-xs text-gray-500 mb-1">在此处完成电子签名</p>
                    <p className="text-xs text-gray-400 italic">（系统将记录您的会员ID及签署时间）</p>
                  </div>
                  <div className="flex items-start gap-2 mb-4">
                    <input type="checkbox" id="agree-check" className="mt-0.5 accent-red-600 w-4 h-4 flex-shrink-0 cursor-pointer" />
                    <label htmlFor="agree-check" className="text-xs text-gray-600 cursor-pointer leading-relaxed">我已仔细阅读上述文件，理解并同意全部条款内容</label>
                  </div>
                  <button onClick={() => setSignDone(true)} className="w-full py-3 bg-red-600 text-white rounded-xl text-sm font-medium hover:bg-red-700 cursor-pointer transition-colors whitespace-nowrap">确认签署</button>
                </>
              ) : (
                <div className="bg-green-50 rounded-xl p-4 text-center">
                  <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
                    <i className="ri-check-double-line text-green-600 text-xl"></i>
                  </div>
                  <p className="text-sm font-bold text-green-700 mb-1">签署成功</p>
                  <p className="text-xs text-green-600">已记录至您的会员档案</p>
                </div>
              )}
              <p className="text-xs text-gray-400 text-center mt-3">此为管理员预览模拟，非真实签署</p>
            </div>
          </div>
        </div>
      )}

      {/* 文档编辑：新建 / 编辑正文 */}
      {showDocEditor && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-5xl max-h-[92vh] flex flex-col shadow-xl overflow-hidden">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 flex-shrink-0">
              <div>
                <h3 className="font-bold text-gray-800">{docEditorTarget ? "编辑文档" : "新建文件"}</h3>
                <p className="text-xs text-gray-400 mt-0.5">
                  左侧编辑正文，右侧为保存后的完整文本预览（只读）；保存为 UTF-8 纯文本并存储至对象存储。
                </p>
              </div>
              <button type="button" onClick={closeDocEditor} className="w-9 h-9 flex items-center justify-center text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100 cursor-pointer flex-shrink-0">
                <i className="ri-close-line text-lg"></i>
              </button>
            </div>
            <div className="flex-1 overflow-y-auto px-6 py-4">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-8 items-start">
                <div className="space-y-4 min-w-0">
                  {docEditorTarget?.requireSignature && (
                    <div className="text-xs text-amber-800 bg-amber-50 border border-amber-100 rounded-xl px-3 py-2 leading-relaxed">
                      该文件要求会员签署。保存新正文后将<strong>清空已有签署记录</strong>，会员需重新签署。
                    </div>
                  )}
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1.5">文件名称 *</label>
                    <input
                      type="text"
                      value={newFile.name}
                      onChange={(e) => setNewFile((p) => ({ ...p, name: e.target.value }))}
                      placeholder="如：入会协议 2026版"
                      disabled={!!docEditorTarget}
                      className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-red-400 disabled:bg-gray-50 disabled:text-gray-500"
                    />
                    {docEditorTarget && <p className="text-[11px] text-gray-400 mt-1">已创建的文件名称不可在此修改</p>}
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1.5">文件类型</label>
                      <select
                        value={newFile.type}
                        disabled={!!docEditorTarget}
                        onChange={(e) => setNewFile((p) => ({ ...p, type: e.target.value as AssocFile["type"] }))}
                        className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-red-400 bg-white disabled:bg-gray-50 disabled:text-gray-500"
                      >
                        {(["contract", "rulebook", "notice", "form", "certificate", "disclaimer"] as const).map((t) => (
                          <option key={t} value={t}>
                            {typeConfig[t].label}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1.5">版本号</label>
                      <input
                        type="text"
                        value={newFile.version}
                        disabled={!!docEditorTarget}
                        onChange={(e) => setNewFile((p) => ({ ...p, version: e.target.value }))}
                        placeholder="如 v1.0"
                        className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-red-400 disabled:bg-gray-50 disabled:text-gray-500"
                      />
                    </div>
                  </div>
                  <div className="flex gap-4 flex-wrap">
                    <label className="flex items-center gap-2 cursor-pointer select-none">
                      <input type="checkbox" checked={newFile.showToMembers} onChange={(e) => setNewFile((p) => ({ ...p, showToMembers: e.target.checked }))} className="w-4 h-4 accent-red-600" />
                      <span className="text-xs text-gray-700">发布后对会员可见</span>
                    </label>
                    <label className={`flex items-center gap-2 select-none ${docEditorTarget ? "cursor-not-allowed opacity-60" : "cursor-pointer"}`}>
                      <input
                        type="checkbox"
                        checked={newFile.requireSignature}
                        disabled={!!docEditorTarget}
                        onChange={(e) => setNewFile((p) => ({ ...p, requireSignature: e.target.checked }))}
                        className="w-4 h-4 accent-red-600"
                      />
                      <span className="text-xs text-gray-700">需要会员电子签署</span>
                    </label>
                  </div>
                  {docEditorTarget && <p className="text-[11px] text-gray-400 -mt-2">是否需签署以创建时为准，此处不可更改</p>}
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1.5">文件描述</label>
                    <textarea
                      value={newFile.description}
                      disabled={!!docEditorTarget}
                      onChange={(e) => setNewFile((p) => ({ ...p, description: e.target.value }))}
                      placeholder="简要说明用途（显示在列表与会员端）"
                      rows={2}
                      className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-red-400 resize-none disabled:bg-gray-50 disabled:text-gray-500"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1.5">文档正文（可编辑）</label>
                    {loadingEditorBody ? (
                      <div className="flex items-center justify-center py-16 text-sm text-gray-400 border border-gray-100 rounded-xl bg-gray-50">
                        <i className="ri-loader-4-line animate-spin mr-2 text-lg"></i> 正在载入现有正文…
                      </div>
                    ) : (
                      <textarea
                        value={editorPlainBody}
                        onChange={(e) => setEditorPlainBody(e.target.value)}
                        placeholder="在此输入正文。空行分段；单行换行在预览中仍会保留。"
                        rows={14}
                        className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-red-400 resize-y min-h-[220px] font-sans leading-relaxed"
                      />
                    )}
                    <p className="text-[11px] text-gray-400 mt-1">保存为 .txt 正文（text/plain）。若链接受跨域限制无法拉取，正文可能为空，可手工粘贴或重试。</p>
                  </div>
                </div>
                <div className="min-w-0 flex flex-col lg:sticky lg:top-0 lg:max-h-[calc(92vh-8rem)]">
                  <div className="flex items-center gap-2 mb-1.5">
                    <label className="block text-xs font-medium text-gray-600">文档编辑预览</label>
                    <span className="text-[10px] text-gray-400 px-1.5 py-0.5 rounded bg-gray-100">只读</span>
                  </div>
                  <p className="text-[11px] text-gray-400 mb-2">与点击「保存」后写入存储的完整文本一致（标题 + 空行 + 左侧正文）。</p>
                  <div
                    className="rounded-xl border border-gray-200 bg-gray-50 overflow-hidden flex flex-col flex-1 min-h-[220px] max-h-[min(55vh,520px)]"
                    aria-readonly="true"
                  >
                    {loadingEditorBody ? (
                      <div className="flex flex-1 items-center justify-center text-sm text-gray-400 py-12">
                        <i className="ri-loader-4-line animate-spin mr-2 text-lg"></i> 载入后再显示预览
                      </div>
                    ) : (
                      <pre className="text-sm text-gray-800 whitespace-pre-wrap break-words font-sans leading-relaxed p-3 overflow-y-auto flex-1 select-none cursor-default">
                        {docEditorPreviewPlain}
                      </pre>
                    )}
                  </div>
                </div>
              </div>
            </div>
            <div className="px-6 py-4 border-t border-gray-100 bg-gray-50 flex justify-end gap-3 flex-shrink-0">
              <button type="button" onClick={closeDocEditor} className="px-5 py-2 text-sm text-gray-600 bg-white border border-gray-200 rounded-xl hover:bg-gray-50 cursor-pointer whitespace-nowrap">
                取消
              </button>
              <button
                type="button"
                disabled={!newFile.name.trim() || savingDoc || loadingEditorBody}
                onClick={() => void handleSaveDocument()}
                className={`px-5 py-2 text-sm rounded-xl cursor-pointer transition-all whitespace-nowrap flex items-center gap-1.5 ${
                  saveDocDone ? "bg-green-500 text-white" : savingDoc ? "bg-gray-200 text-gray-500 cursor-wait" : "bg-red-600 text-white hover:bg-red-700 disabled:opacity-50"
                }`}
              >
                <i className={saveDocDone ? "ri-check-line" : savingDoc ? "ri-loader-4-line animate-spin" : "ri-save-3-line"}></i>
                {saveDocDone ? "已保存" : savingDoc ? "保存中…" : docEditorTarget ? "保存文档" : "创建文件"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
